import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { CardWrapper, Checkbox, Divider } from 'components'
import { TopToolbar, SortableList, SortableItem } from 'components/lists'
import { useEventSlug, useQuery } from 'hooks'
import { _, colors } from 'utils'
import {
    BoothCategoryType,
    BoothsQuery,
    BoothsQueryVariables,
    GetBoothCategoriesQuery,
    GetBoothCategoriesQueryVariables,
    GetMapSpacesQuery,
    GetMapSpacesQueryVariables,
    SpaceTypeEnum,
} from 'generated/graphql'
import { getNodes, convertDataForSortableList } from 'graphql/utils'
import { PickNode } from 'graphql/types'
import { updateEvent } from 'features/generalsettings/graphql/mutation'
import { addModal } from 'features/modals/modalSlice'
import { showNotification } from 'features/notifications'
import { updateBoothOrder } from './graphql/mutation'
import CreateUpdateBooth from './CreateUpdateBooth'
import BoothRow from './components/BoothRow'
import { spacesStatusSelector } from '../events/eventSlice'
import { boothCategoriesQuery, boothsQuery, mapSpacesQuery } from './graphql/query'

const S = {
    Wrapper: styled.div`
        & > div > div:nth-last-child(-n + 3) .popover-menu {
            top: 8px;
            bottom: unset;
            transform: translateY(-100%);
        }
    `,
    BoothRowWrapper: styled.div`
        display: flex;
        align-items: center;
        background: ${colors.darkOpacity(0.05)};
        margin-bottom: 3px;
    `,
}

export type Booth = PickNode<BoothsQuery, 'booths'>

const BoothList = () => {
    const { boothSpaces, maxBooths } = useSelector(spacesStatusSelector)
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const [search, setSearch] = useState('')
    const [linkedFrom, setLinkedFrom] = useState([])
    const [booths, setBooths] = useState<SortableItem[]>([])
    const [minOrder, setMinOrder] = useState(0)
    const disableSort = !!(search || linkedFrom.length)
    const [showBoothSidebar, setShowBoothSidebar] = useState(false)

    const { data, requestStatus, fetchData: callGetBooths } = useQuery<BoothsQuery, BoothsQueryVariables>(
        boothsQuery,
        {
            first: 499,
            eventId: eventSlug,
            search,
            linkedFrom,
        }
    )
    const {data: boothCategories} = useQuery<GetBoothCategoriesQuery, GetBoothCategoriesQueryVariables>(
        boothCategoriesQuery,
        { eventId: eventSlug }
    )
    const categories = boothCategories?.event?.boothCategories || []

    useEffect(() => {
        setShowBoothSidebar(data?.event?.showBoothSidebar)
    }, [data?.event?.showBoothSidebar])

    const showCreateModal = (_categories?: BoothCategoryType[]) => {
        dispatch(
            addModal({
                Component: ({ close }: { close: () => void }) => (
                    <CreateUpdateBooth
                        action="create"
                        close={close}
                        callGetBooths={callGetBooths}
                        minOrder={minOrder}
                        categories={_categories}
                    />
                ),
                header: _('booths.create_booth.modal_header'),
            })
        )
    }

    const { data: mapSpacesResponse } = useQuery<GetMapSpacesQuery, GetMapSpacesQueryVariables>(
        mapSpacesQuery,
        { eventId: eventSlug, type: SpaceTypeEnum.Map }
    )
    const mapSpaces = getNodes(mapSpacesResponse?.event?.spaces)

    const renderItem = (booth) => (
        <BoothRow
            booth={booth}
            disableSort={disableSort}
            callGetBooths={callGetBooths}
            categories={categories}
        />
    )

    useEffect(() => {
        // After fetching data from API successfully, convert data for SortableList
        if (data) {
            const dataForSortableList = convertDataForSortableList(getNodes(data?.event?.booths), renderItem)
            setBooths(dataForSortableList)
            setMinOrder(dataForSortableList[0]?.order || 0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, boothCategories?.event?.boothCategories?.length])

    const updateMinOrder = (order) => setMinOrder(order)

    const updateShowBoothSidebarConfig = async () => {
        await updateEvent(eventSlug, { virtual: { showBoothSidebar: !showBoothSidebar } })
        await callGetBooths()
        dispatch(showNotification({
            type: 'success',
            body: 'Updated',
        }))
    }

    return (
        <CardWrapper>
            <Checkbox
                label={_('booths.show_booth_sidebar')}
                checked={showBoothSidebar}
                onChange={updateShowBoothSidebarConfig}
                className="mb-1"
            />
            <Divider marginBottom={10} />
            <TopToolbar
                addItemText={_('booths.list.add')}
                disabledAdd={boothSpaces >= maxBooths}
                disabledAddTooltip={boothSpaces >= maxBooths && _('booths.list.max_reached')}
                filters={[
                    {
                        placeholder: _('booths.list.filter_by_links'),
                        options: mapSpaces,
                        isMulti: true,
                        onChange: setLinkedFrom,
                        optionValue: 'id',
                        optionLabel: 'name',
                    },
                ]}
                onAddItem={() => showCreateModal(categories)}
                onSearch={setSearch}
                searchPlaceHolder={_('booths.list.search_placeholder')}
            />
            <S.Wrapper>
                <SortableList
                    initialList={booths}
                    updateOrder={updateBoothOrder}
                    disableSort={disableSort}
                    onUpdateMinOrder={updateMinOrder}
                    requestStatus={requestStatus}
                />
            </S.Wrapper>
        </CardWrapper>
    )
}

export default BoothList
